"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.toISOStringWithTimezone = exports.getDiffInWeeks = exports.getDiffInMonths = exports.adjustDate = exports.setDayEnd = exports.setDayStart = exports.getTimezone = exports.setTime = exports.formatTime = exports.getDateOffset = exports.sortableDateFormat = exports.relativeTimeShortFormat = exports.getTimeString = exports.isShortTimeString = exports.parseTime = exports.weekMs = exports.dayMs = exports.hourMs = exports.minuteMs = void 0;
const dayjs_1 = __importDefault(require("dayjs"));
const math_1 = require("../math");
const text_1 = require("../text");
exports.minuteMs = 60 * 1000;
exports.hourMs = 60 * exports.minuteMs;
exports.dayMs = 24 * exports.hourMs;
exports.weekMs = 7 * exports.dayMs;
const spans = [
    { name: 's', div: 1000, limit: 59 },
    { name: ' min', div: 60, limit: 59 },
    { name: 'h', div: 60, limit: 23 },
    { name: 'd', div: 24, limit: 6 },
];
const unitFactorMap = {
    s: 1000,
    m: exports.minuteMs,
    h: exports.hourMs,
    d: exports.dayMs,
    mo: exports.dayMs * 30,
    y: exports.dayMs * 365,
};
function parseTime(time) {
    const quantity = Number.parseInt(time, 10);
    const unit = time.replace(/[\W\d]/gu, '');
    const factor = unitFactorMap[unit];
    if (!factor) {
        throw new Error(`Invalid time unit "${unit}"`);
    }
    return quantity * factor;
}
exports.parseTime = parseTime;
function isShortTimeString(time) {
    return /^[0-9]+([smhdy]|mo)$/u.test(time);
}
exports.isShortTimeString = isShortTimeString;
function getTimeString(date) {
    return new Date(date).toLocaleTimeString(undefined, {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
    });
}
exports.getTimeString = getTimeString;
function relativeTimeShortFormat(date, options) {
    let span = typeof date === 'number' ? date : Math.abs(date.getTime() - Date.now());
    if (span >= 7 * exports.dayMs) {
        if (span < 8 * exports.dayMs) {
            return '1w';
        }
        if (date instanceof Date) {
            const sameYear = date.getFullYear() === new Date().getFullYear();
            return (0, dayjs_1.default)(date).format(sameYear ? 'D MMM' : 'D MMM YYYY');
        }
        return `${span / (7 * exports.dayMs)} weeks`;
    }
    let curr = -1;
    let and = 0;
    do {
        curr += 1;
        const { div } = spans[curr];
        and = span % div;
        span = Math.floor(span / div);
    } while (span > spans[curr].limit);
    const res = `${span}${spans[curr].name}${(options === null || options === void 0 ? void 0 : options.precise) && curr > 0 && and ? ` ${and}${spans[curr - 1].name}` : ''}`;
    if (date instanceof Date && (options === null || options === void 0 ? void 0 : options.includeContext)) {
        const inPast = date.getTime() > Date.now();
        return inPast ? `in ${res}` : `${res} ago`;
    }
    return res;
}
exports.relativeTimeShortFormat = relativeTimeShortFormat;
function english24ToIso(dateString, milliseconds = 0) {
    const newLocal = /((?:\d+\/?)+), ((?:\d+:?)+)/u;
    const match = newLocal.exec(dateString);
    if (!match) {
        throw new Error(`Invalid date (${dateString})`);
    }
    const [, date, time] = match;
    const [mo, d, y] = date.split('/');
    return `${y.length > 4 ? '+' : ''}${(0, text_1.digits)(y, y.length > 4 ? 6 : 4)}-${(0, text_1.digits)(mo, 2)}-${(0, text_1.digits)(d, 2)}T${time.replace(/^24/u, '00')}.${(0, text_1.digits)(milliseconds.toString(), 3)}Z`;
}
function sortableDateFormat(date, timeZone = getTimezone()) {
    const dateObj = new Date(date);
    return english24ToIso(dateObj.toLocaleString('en', { timeZone, hour12: false }), dateObj.getMilliseconds())
        .replace('T', '_')
        .replace('Z', '');
}
exports.sortableDateFormat = sortableDateFormat;
function getDateOffset(date, timeZone) {
    const d0 = date.toLocaleString('en', { timeZone: 'UTC', hour12: false });
    const d1 = date.toLocaleString('en', { timeZone, hour12: false });
    const d0utc = new Date(english24ToIso(d0));
    const d1utc = new Date(english24ToIso(d1));
    const dateOffset = Math.trunc(d1utc.getTime() / exports.dayMs) - Math.trunc(d0utc.getTime() / exports.dayMs);
    const timeZoneOffset = (d1utc.getTime() - d0utc.getTime()) / 1000 / 60;
    return { dateOffset, timeZoneOffset };
}
exports.getDateOffset = getDateOffset;
function formatTime(hours = 0, minutes = 0, seconds = 0) {
    return [hours, minutes, seconds].map((d) => (0, text_1.digits)(d, 2)).join(':');
}
exports.formatTime = formatTime;
function setTime(...args) {
    const restArgs = [...args];
    const timeZone = typeof args[0] === 'string' ? restArgs.shift() : getTimezone();
    const [date, hours, minutes = 0, seconds = 0, milliseconds = 0] = restArgs;
    const newDate = new Date(date);
    const { dateOffset, timeZoneOffset } = getDateOffset(newDate, timeZone);
    const hoursOffset = Math.trunc(timeZoneOffset / 60);
    const minutesOffset = timeZoneOffset % 60;
    newDate.setUTCDate(newDate.getUTCDate() + dateOffset);
    newDate.setUTCHours(hours - hoursOffset);
    newDate.setUTCMinutes(minutes - minutesOffset);
    newDate.setUTCSeconds(seconds);
    newDate.setUTCMilliseconds(milliseconds);
    const timeChange = getDateOffset(newDate, timeZone).timeZoneOffset - timeZoneOffset;
    if (timeChange) {
        newDate.setUTCMinutes(newDate.getUTCMinutes() - timeChange);
        if (timeChange > 0 &&
            newDate
                .toLocaleTimeString('en', { timeZone, hour12: false })
                .replace(/^24/u, '00') !== formatTime(hours, minutes, seconds)) {
            newDate.setUTCMinutes(newDate.getUTCMinutes() + timeChange);
        }
    }
    return newDate;
}
exports.setTime = setTime;
function getTimezone() {
    return new Intl.DateTimeFormat().resolvedOptions().timeZone;
}
exports.getTimezone = getTimezone;
function setDayStart(date, timeZone = getTimezone()) {
    return setTime(timeZone, date, 0);
}
exports.setDayStart = setDayStart;
function setDayEnd(date, timeZone = getTimezone()) {
    return setTime(timeZone, date, 23, 59, 59, 999);
}
exports.setDayEnd = setDayEnd;
function adjustDate(date, days = 0, months = 0, years = 0) {
    const newDate = new Date(date);
    newDate.setUTCDate(newDate.getUTCDate() + days);
    newDate.setUTCMonth(newDate.getUTCMonth() + months);
    newDate.setUTCFullYear(newDate.getUTCFullYear() + years);
    return newDate;
}
exports.adjustDate = adjustDate;
function getDiffInMonths(from, to) {
    const fromD = new Date(from);
    const toD = new Date(to);
    const [date1, date2] = [fromD, toD].sort(math_1.ascSort);
    let diff = (date2.getUTCFullYear() - date1.getUTCFullYear()) * 12 +
        date2.getUTCMonth() -
        date1.getUTCMonth();
    while (adjustDate(date1, 0, diff) > date2) {
        diff -= 1;
    }
    return (fromD > toD ? -1 : 1) * diff;
}
exports.getDiffInMonths = getDiffInMonths;
function getDiffInWeeks(from, to) {
    const diff = (new Date(to).getTime() - new Date(from).getTime()) / exports.weekMs;
    const sign = diff < 0 ? -1 : 1;
    return sign * Math.floor(Math.abs(diff));
}
exports.getDiffInWeeks = getDiffInWeeks;
function toISOStringWithTimezone(date = new Date()) {
    const time = date.toLocaleTimeString('pl', { hour12: false });
    const [, ms] = performance.now().toFixed(6).split('.');
    const offset = new Date().getTimezoneOffset() * parseTime('1m');
    const sign = offset < 0 ? '+' : '-';
    const offsetHours = (0, text_1.digits)(Math.floor(Math.abs(offset) / parseTime('1h')), 2);
    const offsetMinutes = (0, text_1.digits)(Math.abs(offset) % parseTime('1h'), 2);
    const [dateString] = new Date(date.getTime() - offset)
        .toISOString()
        .split('T');
    return `${dateString}T${time}.${ms}${sign}${offsetHours}:${offsetMinutes}`;
}
exports.toISOStringWithTimezone = toISOStringWithTimezone;
